import { useState } from "react";
import { speak } from "@/lib/speech";
import { KANA } from "./kana";
import { WritingPad } from "./WritingPad";

const CharTile = ({ c, active, onClick, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`rounded-2xl border-2 p-2 flex flex-col items-center transition-[transform,border-color] hover:-translate-y-0.5 active:scale-95 ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-light))]" : "border-slate-200 bg-white"}`}
  >
    <span className="font-display text-2xl font-extrabold text-slate-900">{c.char}</span>
    <span className="text-[11px] font-semibold text-[hsl(var(--accent-blue))]">{c.romaji}</span>
  </button>
);

const KanaChart = ({ lang, active, onPick }) => {
  const [set, setSet] = useState("hiragana");
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["hiragana", "katakana"].map((s) => (
          <button key={s} data-testid={`kana-tab-${s}`} onClick={() => setSet(s)} className={`px-4 py-2 rounded-full text-sm font-bold capitalize ${set === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>{s}</button>
        ))}
      </div>
      <p className="text-sm text-slate-500 mb-3">
        {set === "hiragana" ? "Hiragana is used for native Japanese words and grammar. Learn it first." : "Katakana is used for foreign words, names and emphasis, like コーヒー (koohii, coffee)."} Tap any character to hear it and practise writing it.
      </p>
      <div className="space-y-2" data-testid={`kana-chart-${set}`}>
        {KANA[set].map((row, i) => (
          <div key={i} className="grid grid-cols-5 gap-2">
            {row.map((c) => (
              <CharTile key={c.char} c={c} active={active?.char === c.char} testId={`kana-${c.romaji}-${set}`} onClick={() => { onPick(c); speak(c.char, lang); }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const ScriptSection = ({ characters = [], lang }) => {
  const isJa = lang === "ja";
  const [active, setActive] = useState(isJa ? KANA.hiragana[0][0] : characters[0]);
  return (
    <section>
      <h2 className="font-display text-xl font-bold text-slate-900 mt-10 mb-1">{isJa ? "Hiragana & Katakana" : "Letters & spelling"}</h2>
      <p className="text-sm text-slate-500 mb-4">Learn how each one sounds, then trace it on the writing pad.</p>
      <div className="grid lg:grid-cols-5 gap-6 items-start">
        <div className="lg:col-span-3">
          {isJa ? (
            <KanaChart lang={lang} active={active} onPick={setActive} />
          ) : null}
          {characters.length > 0 && (
            <div className={isJa ? "mt-6" : ""}>
              {isJa && <h3 className="font-display font-bold text-slate-900 mb-3">Today's focus characters</h3>}
              <div className="grid sm:grid-cols-2 gap-3">
                {characters.map((c, i) => (
                  <button key={i} data-testid={`script-char-${i}`} onClick={() => { setActive(c); speak(c.example || c.char, lang); }}
                    className={`text-left rounded-2xl border-2 p-4 flex gap-3 transition-[border-color] ${active?.char === c.char ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-light))]" : "border-slate-200 bg-white"}`}>
                    <span className="min-w-[3rem] font-display text-3xl font-extrabold text-slate-900">{c.char}</span>
                    <span className="text-sm">
                      {c.romaji && <span className="block font-semibold text-[hsl(var(--accent-blue))]">{c.romaji}</span>}
                      <span className="block text-slate-700">{c.sound}</span>
                      {c.example && <span className="block text-slate-500 text-xs mt-1">{c.example} · {c.translation}</span>}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-2 lg:sticky lg:top-24">
          {active && <WritingPad char={active.char} hint={active.writing_tip || (active.romaji ? `Says "${active.romaji}"` : "")} lang={lang} />}
        </div>
      </div>
    </section>
  );
};
